import React, { useCallback, useEffect, useState } from 'react';
import { AddTopicGroupModal } from './AddTopicGroupModal';
import { EditTopicGroupModal } from '../Traffic/Settings/EditTopicGroupModal';
import { SlDialogRoot } from 'src/components/BeaconV2Components/SlDialog';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { GenericErrorMessage } from 'src/components/BeaconV2Components/common/CommonSnackbarMessages/GenericErrorMessage';
import { useSnackbar, useAppSelector } from 'src/utils/Hooks';
import {
  useAddTopicGroupStoreActions,
  useAddTopicGroupStoreState
} from 'src/zustandStores/BeaconPro/Topics/TopicGroups/useAddTopicGroupStore';
import { TOPIC_QUERY_KEYS } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/topicQueryKeys';
import { useTopicPolling } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/hooks/useTopicPolling';
import {
  CreateTopicGroupPayload,
  topicSettingsServerProxy
} from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/serverProxy';

/**
 * Manages the flow for adding a topic group. Takes care of state for each of the two modals
 * and saves the new topic group.
 */
export function AddTopicGroupFlowManager() {
  const { setTopicGroupState, closeModal } = useAddTopicGroupStoreActions();
  const { isOpen, topicGroupState } = useAddTopicGroupStoreState();
  const { name, selectedCategoryIds, selectedSubcategoryIds } = topicGroupState;

  const [modal, setModal] = useState<'addTopicGroup' | 'addTopicsToGroup'>(null);

  const { pollTopicGroupUpdate } = useTopicPolling();

  const queryClient = useQueryClient();
  const retailerId = useAppSelector((state) => state.retailer.id);
  const { showSnackbar } = useSnackbar();
  const { mutateAsync: createTopicGroupMutation, isLoading } = useMutation({
    mutationFn: async (payload: CreateTopicGroupPayload) => {
      const response = await topicSettingsServerProxy.createTopicGroup(payload);
      // Return topic group id to be used in polling
      return response;
    },
    onSuccess: (topicGroupId) => {
      queryClient.invalidateQueries([TOPIC_QUERY_KEYS.all]);
      pollTopicGroupUpdate(topicGroupId, `creating-topicGroup-${topicGroupId}`);
    }
  });

  const handleClear = useCallback(() => {
    setTopicGroupState(({ clear }) => clear());
  }, [setTopicGroupState]);

  const handleClose = () => {
    closeModal();
    handleClear();
  };

  useEffect(() => {
    if (isOpen) {
      setModal('addTopicGroup');
    } else {
      setModal(null);
      handleClear();
    }
  }, [isOpen, handleClear]);

  const handleCreateTopicGroup = async (selectedTopicIds: string[], clearTopics: () => void) => {
    if (isLoading) {
      return;
    }

    try {
      await createTopicGroupMutation({
        topicGroupName: name.trim(),
        topicIds: selectedTopicIds,
        retailerId: +retailerId,
        includedCategoryIds: selectedCategoryIds.map((id) => +id),
        includedSubCategoryIds: selectedSubcategoryIds.map((id) => +id)
      });
      showSnackbar({
        message: "You've successfully created a topic group.",
        type: 'success'
      });
    } catch (error) {
      console.error('Error creating topic group', error);
      showSnackbar({
        message: <GenericErrorMessage />,
        type: 'error'
      });
    } finally {
      closeModal();
      handleClear();
      clearTopics();
    }
  };

  return (
    <SlDialogRoot>
      <AddTopicGroupModal
        open={modal === 'addTopicGroup'}
        onClose={() => handleClose()}
        handleNext={() => setModal('addTopicsToGroup')}
      />
      <EditTopicGroupModal
        title="Add Topic to Group"
        footer={({ selectedTopicIds, handleClear: clearTopics }) => (
          <Flex justifyContent="space-between">
            <SlButton onClick={() => clearTopics()}>Clear</SlButton>
            <Flex gap="md">
              <SlButton onClick={() => setModal('addTopicGroup')}>Back</SlButton>
              <SlButton
                disabled={selectedTopicIds.length < 1}
                variant="contained"
                onClick={() => handleCreateTopicGroup(selectedTopicIds, clearTopics)}
              >
                {isLoading ? 'Saving...' : 'Save'}
              </SlButton>
            </Flex>
          </Flex>
        )}
        open={modal === 'addTopicsToGroup'}
        onClose={() => handleClose()}
      />
    </SlDialogRoot>
  );
}
